import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  TrendingUp, Users, Wrench, Briefcase, Calendar as CalendarIcon, X, Mic2, Filter,
  Edit2, Save, RotateCcw,
} from 'lucide-react';
import { HorizontalScroller } from '../HorizontalScroller';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const YEARS = [2023, 2024, 2025, 2026, 2027];

const money = (v) => `R$ ${(v ?? 0).toFixed(2).replace('.', ',')}`;

const formatDeadline = (d) => {
  if (!d) return '—';
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return d;
  }
};

const ForecastCard = ({ label, value, subValue, Icon, iconColor, testid }) => (
  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5" data-testid={testid}>
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
      <Icon className={`h-5 w-5 ${iconColor}`} />
    </div>
    <p className="text-2xl font-bold text-white font-mono">{value}</p>
    {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
  </div>
);

const parseNum = (val) => {
  if (val === '' || val === null || val === undefined) return null;
  const n = parseFloat(String(val).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const DubladorRow = ({ entry, projectId, onSaved, canEdit }) => {
  const { getAuthHeaders } = useAuth();
  const [editing, setEditing] = useState(false);
  const [falas, setFalas] = useState(String(entry.num_falas ?? 0));
  const [amount, setAmount] = useState(String(entry.amount ?? 0));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFalas(String(entry.num_falas ?? 0));
    setAmount(String(entry.amount ?? 0));
  }, [entry.num_falas, entry.amount]);

  const startEdit = () => {
    setFalas(String(entry.num_falas ?? 0));
    setAmount(String(entry.amount ?? 0));
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setFalas(String(entry.num_falas ?? 0));
    setAmount(String(entry.amount ?? 0));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        character_id: entry.character_id,
        user_id: entry.user_id,
      };
      const nf = parseInt(falas, 10);
      if (!Number.isNaN(nf) && nf !== entry.num_falas) body.num_falas = nf;
      const amt = parseNum(amount);
      if (amt !== null && Math.abs(amt - entry.amount) > 0.0001) body.amount = amt;
      if (Object.keys(body).length === 2) {
        // nothing changed
        setEditing(false);
        return;
      }
      await axios.put(`${API}/finance-dashboard/forecast/${projectId}/dublador`, body,
        { headers: getAuthHeaders() });
      toast.success('Atualizado!');
      setEditing(false);
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/finance-dashboard/forecast/${projectId}/dublador`,
        { character_id: entry.character_id, user_id: entry.user_id, reset_amount: true },
        { headers: getAuthHeaders() });
      toast.success('Override removido — voltou ao valor padrão.');
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr
      className="border-t border-slate-700/50 hover:bg-slate-800/30"
      data-testid={`forecast-dublador-row-${entry.character_id}-${entry.user_id}`}
    >
      <td className="px-3 py-2 text-white">{entry.character_name}</td>
      <td className="px-3 py-2 text-slate-300">{entry.user_name || '—'}</td>
      <td className="px-3 py-2 text-right font-mono">
        {editing ? (
          <input
            type="number" min="0" value={falas}
            onChange={e => setFalas(e.target.value)}
            className="bg-slate-800 border border-teal-500 rounded px-2 py-0.5 w-20 text-right text-white text-sm focus:outline-none"
            data-testid={`forecast-dub-falas-input-${entry.character_id}-${entry.user_id}`}
          />
        ) : (
          <span className="text-slate-400">{entry.num_falas}</span>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono">
        {editing ? (
          <input
            type="number" step="0.01" min="0" value={amount}
            onChange={e => setAmount(e.target.value)}
            className="bg-slate-800 border border-emerald-500 rounded px-2 py-0.5 w-28 text-right text-white text-sm focus:outline-none"
            data-testid={`forecast-dub-amount-input-${entry.character_id}-${entry.user_id}`}
          />
        ) : (
          <span className={`${entry.is_overridden ? 'text-amber-300' : 'text-emerald-400'}`}>
            {money(entry.amount)}
            {entry.is_overridden && <span className="text-[10px] text-amber-500 ml-1">●</span>}
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-right whitespace-nowrap">
        {canEdit && !editing && (
          <div className="flex items-center gap-1 justify-end">
            <button
              onClick={startEdit}
              className="text-slate-400 hover:text-teal-400 p-1"
              title="Editar"
              data-testid={`forecast-dub-edit-${entry.character_id}-${entry.user_id}`}
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
            {entry.is_overridden && (
              <button
                onClick={reset}
                disabled={saving}
                className="text-slate-400 hover:text-amber-400 p-1"
                title={`Resetar (padrão R$ ${entry.default_amount?.toFixed(2)})`}
                data-testid={`forecast-dub-reset-${entry.character_id}-${entry.user_id}`}
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-1 justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="text-emerald-400 hover:text-emerald-300 p-1"
              title="Salvar"
              data-testid={`forecast-dub-save-${entry.character_id}-${entry.user_id}`}
            >
              <Save className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={cancel}
              disabled={saving}
              className="text-slate-400 hover:text-white p-1"
              title="Cancelar"
              data-testid={`forecast-dub-cancel-${entry.character_id}-${entry.user_id}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

const StaffRow = ({ entry, projectId, onSaved, canEdit }) => {
  const { getAuthHeaders } = useAuth();
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(String(entry.amount ?? 0));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setAmount(String(entry.amount ?? 0)); }, [entry.amount]);

  const save = async () => {
    setSaving(true);
    try {
      const amt = parseNum(amount);
      if (amt === null || Math.abs(amt - entry.amount) < 0.0001) {
        setEditing(false);
        return;
      }
      await axios.put(`${API}/finance-dashboard/forecast/${projectId}/staff`,
        { user_id: entry.user_id, role_key: entry.role_key, amount: amt },
        { headers: getAuthHeaders() });
      toast.success('Atualizado!');
      setEditing(false);
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/finance-dashboard/forecast/${projectId}/staff`,
        { user_id: entry.user_id, role_key: entry.role_key, reset_amount: true },
        { headers: getAuthHeaders() });
      toast.success('Override removido — voltou ao valor padrão.');
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr
      className="border-t border-slate-700/50 hover:bg-slate-800/30"
      data-testid={`forecast-staff-row-${entry.user_id}-${entry.role_key}`}
    >
      <td className="px-3 py-2 text-white">{entry.user_name || '—'}</td>
      <td className="px-3 py-2 text-slate-300">{entry.role_label}</td>
      <td className="px-3 py-2 text-right font-mono">
        {editing ? (
          <input
            type="number" step="0.01" min="0" value={amount}
            onChange={e => setAmount(e.target.value)}
            className="bg-slate-800 border border-emerald-500 rounded px-2 py-0.5 w-28 text-right text-white text-sm focus:outline-none"
            data-testid={`forecast-staff-amount-input-${entry.user_id}-${entry.role_key}`}
          />
        ) : (
          <span className={`${entry.is_overridden ? 'text-amber-300' : 'text-emerald-400'}`}>
            {money(entry.amount)}
            {entry.is_overridden && <span className="text-[10px] text-amber-500 ml-1">●</span>}
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-right whitespace-nowrap">
        {canEdit && !editing && (
          <div className="flex items-center gap-1 justify-end">
            <button
              onClick={() => setEditing(true)}
              className="text-slate-400 hover:text-teal-400 p-1"
              title="Editar"
              data-testid={`forecast-staff-edit-${entry.user_id}-${entry.role_key}`}
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
            {entry.is_overridden && (
              <button
                onClick={reset}
                disabled={saving}
                className="text-slate-400 hover:text-amber-400 p-1"
                title={`Resetar (padrão R$ ${entry.default_amount?.toFixed(2)})`}
                data-testid={`forecast-staff-reset-${entry.user_id}-${entry.role_key}`}
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-1 justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="text-emerald-400 hover:text-emerald-300 p-1"
              data-testid={`forecast-staff-save-${entry.user_id}-${entry.role_key}`}
            >
              <Save className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => { setEditing(false); setAmount(String(entry.amount ?? 0)); }}
              disabled={saving}
              className="text-slate-400 hover:text-white p-1"
              data-testid={`forecast-staff-cancel-${entry.user_id}-${entry.role_key}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

const ForecastDetailsModal = ({ project, onClose, onRefetch, canEdit }) => {
  if (!project) return null;
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="forecast-details-modal"
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-white" data-testid="forecast-modal-title">
              {project.project_title}
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              {project.client && <span>{project.client} · </span>}
              Deadline: {formatDeadline(project.delivery_deadline)}
              {project.episodes > 0 && <span> · {project.episodes} ep.</span>}
              {project.duration_display && <span> · {project.duration_display}</span>}
            </p>
            {canEdit && (
              <p className="text-[11px] text-amber-400/80 mt-1.5 flex items-center gap-1">
                <Edit2 className="h-3 w-3" />
                Clique no lápis para editar falas/valores. Vozerios (mais de 1 dublador no mesmo personagem) pagam o valor integral a cada participante.
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
            data-testid="forecast-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="p-6 space-y-6">
          {/* Totais */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-center">
              <p className="text-[10px] uppercase text-slate-400 tracking-wider">Dubladores</p>
              <p className="text-lg font-mono font-bold text-teal-300 mt-1">{money(project.dubladores_total)}</p>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-center">
              <p className="text-[10px] uppercase text-slate-400 tracking-wider">Staff</p>
              <p className="text-lg font-mono font-bold text-purple-300 mt-1">{money(project.staff_total)}</p>
            </div>
            <div className="bg-slate-800/60 border border-teal-500/40 rounded-lg p-3 text-center">
              <p className="text-[10px] uppercase text-slate-400 tracking-wider">Total</p>
              <p className="text-lg font-mono font-bold text-white mt-1">{money(project.total)}</p>
            </div>
          </div>

          {/* Dubladores */}
          <section>
            <h4 className="text-sm font-semibold text-teal-300 mb-2 flex items-center gap-2">
              <Mic2 className="h-4 w-4" />
              Dubladores ({project.dubladores.length})
            </h4>
            {project.dubladores.length === 0 ? (
              <p className="text-xs text-slate-500 italic">Nenhum dublador atribuído com falas contabilizáveis.</p>
            ) : (
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/60 text-slate-400 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Personagem</th>
                      <th className="text-left px-3 py-2">Dublador</th>
                      <th className="text-right px-3 py-2">Falas</th>
                      <th className="text-right px-3 py-2">Valor</th>
                      <th className="text-right px-2 py-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.dubladores.map((d, idx) => (
                      <DubladorRow
                        key={`${d.character_id}-${d.user_id}-${idx}`}
                        entry={d}
                        projectId={project.project_id}
                        onSaved={onRefetch}
                        canEdit={canEdit}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Staff */}
          <section>
            <h4 className="text-sm font-semibold text-purple-300 mb-2 flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Equipe ({project.staff.length})
            </h4>
            {project.staff.length === 0 ? (
              <p className="text-xs text-slate-500 italic">
                Nenhum staff atribuído ou duração do projeto não definida.
              </p>
            ) : (
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/60 text-slate-400 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Nome</th>
                      <th className="text-left px-3 py-2">Função</th>
                      <th className="text-right px-3 py-2">Valor</th>
                      <th className="text-right px-2 py-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.staff.map((s, idx) => (
                      <StaffRow
                        key={`${s.user_id}-${s.role_key}-${idx}`}
                        entry={s}
                        projectId={project.project_id}
                        onSaved={onRefetch}
                        canEdit={canEdit}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const ForecastSection = () => {
  const { user, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pendente'); // 'pendente' | 'entregue' | 'todos'
  const [year, setYear] = useState(''); // '' = todos os anos
  const [month, setMonth] = useState(''); // '' = todos os meses

  const canEdit = user?.role === 'dono';

  const fetchForecast = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ status_filter: statusFilter });
    if (year) params.append('year', year);
    if (month) params.append('month', month);
    try {
      const res = await axios.get(`${API}/finance-dashboard/forecast?${params}`, { headers: getAuthHeaders() });
      setData(res.data);
      // Keep selected project in sync with new data
      setSelected(prev => prev ? (res.data?.projects?.find(p => p.project_id === prev.project_id) || null) : null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, year, month, getAuthHeaders]);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);

  const hasActiveFilter = statusFilter !== 'pendente' || year || month;

  return (
    <section className="space-y-4" data-testid="forecast-section">
      <div className="flex items-center gap-3">
        <TrendingUp className="h-6 w-6 text-amber-400" />
        <div>
          <h2 className="text-xl font-semibold text-white">Previsão de Gastos Futuros</h2>
          <p className="text-xs text-slate-400">
            Projetos filtráveis por deadline e status de entrega — soma dubladores + staff.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="forecast-filters">
        <span className="flex items-center gap-1.5 text-xs text-slate-400 uppercase tracking-wider px-2">
          <Filter className="h-3.5 w-3.5" />
          Filtros
        </span>

        {/* Status filter */}
        <div className="inline-flex rounded-lg bg-slate-900/50 border border-slate-700 overflow-hidden" data-testid="forecast-status-filter">
          {[
            { v: 'pendente', label: 'Pendentes', cls: 'text-amber-300 bg-amber-500/20' },
            { v: 'entregue', label: 'Entregues', cls: 'text-emerald-300 bg-emerald-500/20' },
            { v: 'todos',    label: 'Todos',     cls: 'text-slate-200 bg-slate-600/40' },
          ].map(opt => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setStatusFilter(opt.v)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                statusFilter === opt.v ? opt.cls : 'text-slate-400 hover:text-slate-200'
              }`}
              data-testid={`forecast-status-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Year filter */}
        <select
          value={year}
          onChange={e => setYear(e.target.value)}
          className="bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
          data-testid="forecast-year-filter"
        >
          <option value="">Todos os anos</option>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        {/* Month filter */}
        <select
          value={month}
          onChange={e => setMonth(e.target.value)}
          className="bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
          data-testid="forecast-month-filter"
        >
          <option value="">Todos os meses</option>
          {MONTHS.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
        </select>

        {hasActiveFilter && (
          <button
            type="button"
            onClick={() => { setStatusFilter('pendente'); setYear(''); setMonth(''); }}
            className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded transition-colors inline-flex items-center gap-1"
            data-testid="forecast-clear-filters"
          >
            <X className="h-3 w-3" /> Limpar
          </button>
        )}

        <span className="text-xs text-slate-500 ml-auto">
          Filtra pelo deadline do projeto. Projetos sem deadline sempre aparecem.
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-amber-400" />
        </div>
      ) : !data ? (
        <p className="text-slate-400 text-sm">Erro ao carregar previsão.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="forecast-metric-cards">
            <ForecastCard
              label="Total Geral"
              value={money(data.total_general)}
              Icon={TrendingUp}
              iconColor="text-amber-400"
              testid="forecast-total-general"
            />
            <ForecastCard
              label="Dubladores"
              value={money(data.total_dubladores)}
              Icon={Mic2}
              iconColor="text-teal-400"
              testid="forecast-total-dubladores"
            />
            <ForecastCard
              label="Staff"
              value={money(data.total_staff)}
              Icon={Wrench}
              iconColor="text-purple-400"
              testid="forecast-total-staff"
            />
            <ForecastCard
              label="Projetos Ativos"
              value={String(data.active_projects_count)}
              subValue={data.active_projects_count === 1 ? 'com pendência' : 'com pendências'}
              Icon={Briefcase}
              iconColor="text-blue-400"
              testid="forecast-active-count"
            />
          </div>

          {data.projects.length === 0 ? (
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-10 text-center" data-testid="forecast-empty">
              <Briefcase className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">Nenhum projeto ativo com pendências financeiras.</p>
              <p className="text-xs text-slate-500 mt-1">
                Os valores aparecem aqui quando há personagens atribuídos ou duração + equipe de produção.
              </p>
            </div>
          ) : (
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl overflow-hidden" data-testid="forecast-table-wrap">
              <HorizontalScroller layout="block" fadeColor="from-slate-800">
                <table className="w-full text-sm min-w-[680px]">
                  <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-3">Projeto</th>
                      <th className="text-left px-4 py-3">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          Deadline
                        </span>
                      </th>
                      <th className="text-right px-4 py-3">
                        <span className="flex items-center gap-1 justify-end">
                          <Users className="h-3 w-3" />
                          Dubladores
                        </span>
                      </th>
                      <th className="text-right px-4 py-3">
                        <span className="flex items-center gap-1 justify-end">
                          <Wrench className="h-3 w-3" />
                          Staff
                        </span>
                      </th>
                      <th className="text-right px-4 py-3">Total</th>
                      <th className="text-center px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.projects.map((p) => (
                      <tr
                        key={p.project_id}
                        className="border-t border-slate-700/50 hover:bg-slate-800/40"
                        data-testid={`forecast-row-${p.project_id}`}
                      >
                        <td className="px-4 py-3">
                          <p className="text-white font-medium">{p.project_title}</p>
                          {p.client && <p className="text-xs text-slate-500">{p.client}</p>}
                        </td>
                        <td className="px-4 py-3 text-slate-300 font-mono text-xs">
                          {formatDeadline(p.delivery_deadline)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-teal-300">
                          {money(p.dubladores_total)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-purple-300">
                          {money(p.staff_total)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold text-white">
                          {money(p.total)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setSelected(p)}
                            className="text-xs px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors"
                            data-testid={`forecast-details-btn-${p.project_id}`}
                          >
                            Ver detalhes
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-900/60 border-t border-slate-700">
                    <tr>
                      <td className="px-4 py-3 text-slate-400 text-xs uppercase tracking-wider" colSpan={2}>
                        Totais
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-teal-300">
                        {money(data.total_dubladores)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-purple-300">
                        {money(data.total_staff)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-amber-300">
                        {money(data.total_general)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </HorizontalScroller>
            </div>
          )}
        </>
      )}

      <ForecastDetailsModal
        project={selected}
        onClose={() => setSelected(null)}
        onRefetch={fetchForecast}
        canEdit={canEdit}
      />
    </section>
  );
};

export default ForecastSection;
